<template>
  <div>
    <label>{{ title }}</label>
    <div class="d-flex">
      <div :class="`d-flex time-component ${disabled ? 'disabled' : ''}`">
        <div>
          <RsDropDownScroll
            :selected="localStart"
            :pick-options="startTimeOptions"
            :is-plain-options="$i18n.locale === 'en'"
            class="ml-2 w-100"
            :disabled="disabled"
            @onselect="handleStartSelect"
          />
        </div>
        <label class="to">{{ $ts('toursSettings.common.to') }}</label>
        <div class="mr-3">
          <RsDropDownScroll
            :selected="localEnd || endTimeList[0]"
            :pick-options="endTimeOptions"
            :is-plain-options="$i18n.locale === 'en'"
            class="w-100"
            :disabled="disabled"
            @onselect="handleEndSelect"
          />
        </div>
      </div>
      <div class="clock">
        <i class="far fa-clock" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ISelectOption } from '@/shared';

interface ITimeSlot {
  startTime: string,
  endTime: string,
}
export default defineComponent({
  name: 'RsTimeInterval',
  props: {
    /**
     * increment of time interval in minutes.
     */
    increment: {
      type: Number,
      default: 30,
    },
    /**
     * `v-model` bound value (Vue 3). Supersedes `value` when provided.
     */
    modelValue: {
      type: Object as () => ITimeSlot,
      default: null,
    },
    /**
     * `v-model` bound value that determines the start and end time for the interval.
     */
    value: {
      type: Object as () => ITimeSlot,
      default: () => ({ startTime: '10:00 am', endTime: '5:00 pm' } as ITimeSlot),
    },
    /**
     * Title of the field
     */
    title: {
      type: String,
      default: 'Select Time Component',
    },
    /**
     * Start Time Cut Off - will remove all the time options before the specified value
     */
    startTimeCutOff: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['change', 'update:modelValue'],
  data() {
    const slot: ITimeSlot = this.modelValue ?? this.value;
    return {
      localStart: slot.startTime,
      localEnd: slot.endTime,
      startTimeList: [] as string[],
      endTimeList: [] as string[],
      dropdown: false,
    };
  },
  computed: {
    /**
     * The externally-bound time slot, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): ITimeSlot {
      return this.modelValue ?? this.value;
    },
    startTimeOptions(): Array<string | ISelectOption> {
      if (this.$i18n.locale === 'en') return this.startTimeList;
      return this.startTimeList.map((t) => ({ value: t, text: this.toFrenchTime(t) }));
    },
    endTimeOptions(): Array<string | ISelectOption> {
      if (this.$i18n.locale === 'en') return this.endTimeList;
      return this.endTimeList.map((t) => ({ value: t, text: this.toFrenchTime(t) }));
    },
    /**
      * Generate an array of time starting at 12:00 am to 11:59pm by `this.increment`
      */
    getTimeArray() {
      return Array(((24 * 60) / this.increment) + 1)
        .fill(0)
        .map((_, index) => {
          const totalMinutes = index * this.increment;
          const hours = Math.floor(totalMinutes / 60);
          const minutes = (totalMinutes / 60 - hours) * 60;
          const minuteString = minutes <= 9 ? `0${minutes}` : minutes.toString();
          return `${hours % 12 === 0 ? 12 : hours % 12}:${minuteString} ${
            hours < 12 ? 'am' : 'pm'
          }`;
        });
    },
  },
  watch: {
    // `localStart`/`localEnd` are seeded from the prop in `data()`, so without this the
    // component would ignore every later parent-driven change (e.g. the agent-settings
    // timezone dropdown rewriting each interval into the newly selected timezone).
    boundValue: {
      deep: true,
      handler(slot: ITimeSlot | null): void {
        if (!slot || (slot.startTime === this.localStart && slot.endTime === this.localEnd)) {
          return;
        }
        this.localStart = slot.startTime;
        this.localEnd = slot.endTime;
        this.refreshTimeLists();
      },
    },
  },
  mounted() {
    this.refreshTimeLists();
  },
  methods: {
    /**
     * Select Start Value. Adjust end time array to only pick start + 1 item.
     */
    handleStartSelect(item: string) {
      this.localStart = item;
      this.endTimeList = this.getTimeArray.slice(
        this.getTimeArray.indexOf(item) + 1,
      );
      [this.localEnd] = this.endTimeList;
      this.$emit('update:modelValue', { startTime: this.localStart, endTime: this.localEnd });
      this.$emit('change', { startTime: this.localStart, endTime: this.localEnd });
    },
    handleEndSelect(item: string) {
      this.localEnd = item;
      this.$emit('update:modelValue', { startTime: this.localStart, endTime: this.localEnd });
      this.$emit('change', { startTime: this.localStart, endTime: this.localEnd });
    },
    showDropdown(): void {
      this.dropdown = !this.dropdown;
    },
    initData() {
      this.startTimeList = this.getTimeArray.slice(0, this.getTimeArray.length - 1);
      this.endTimeList = this.getTimeArray.slice(
        this.getTimeArray.indexOf(this.localStart) + 1,
      );
    },
    /**
     * Rebuilds the start/end option lists from the current `localStart`, applying
     * the `startTimeCutOff` when one is set.
     */
    refreshTimeLists(): void {
      this.initData();
      if (this.startTimeCutOff) {
        this.startTimeList = this.startTimeList.splice(this.startTimeList.indexOf(this.startTimeCutOff));
      }
    },
    toFrenchTime(time: string): string {
      const match = time.match(/^(\d+):(\d+)\s*(am|pm)$/i);
      if (!match) return time;
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const period = match[3].toLowerCase();
      if (period === 'am') {
        hours = hours === 12 ? 0 : hours;
      } else {
        hours = hours === 12 ? 12 : hours + 12;
      }
      return `${hours}h${minutes}`;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";
.wrapper {
  width: 150px;
}
.time-component {
  border: 1px solid $tertiary-gray;
  border-radius: 5px 0 0 5px;
  margin: 0;
  padding: 0;
  height: 41px;
}
.to {
  padding: 0.6rem 1.2rem 0.4rem 1rem;
}
.clock {
  height: 41px;
  width: 51px;
  padding-top: 0.5rem;
  text-align: center;
  margin-left: -0.03rem;
  border-radius: 0px 4px 4px 0;
  background-color: $secondary-color;
  color: white;
}
</style>
